import React, { useState } from "react";
import { Check, Copy, Cpu } from "lucide-react";
import { type Pipeline, PIPELINES } from "constants/pipelines";

import { HoneycombBackground } from "./HoneycombBackground";
import { GridBackground } from "./grid-background";
import { Badge } from "components/Graphs/Badge";
import { Button } from "components/Graphs/Button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "components/Graphs/Dialogue";
import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/24/solid";
import Azure from "assets/img/svg/clouds/azure.svg?react";
import AgentClaudeCode from "assets/img/svg/agents/claude-color.svg";
import AgentCodex from "assets/img/svg/agents/codex-color.svg";
import AgentOpenClaw from "assets/img/svg/agents/openclaw-logo.svg";

export type { Pipeline };
export { PIPELINES };

const AGENT_TOOLS: { name: string; logo: string }[] = [
  { name: "Codex", logo: AgentCodex },
  { name: "Claude Code", logo: AgentClaudeCode },
  { name: "OpenClaw", logo: AgentOpenClaw },
];

type CloneTab = "https" | "ssh" | "cli";

const CLONE_VALUES: Record<CloneTab, string> = {
  https: "https://github.com/NVIDIA/skills.git",
  ssh: "git@github.com:NVIDIA/skills.git",
  cli: "gh repo clone NVIDIA/skills",
};
const DOWNLOAD_SKILLS_URL =
  "https://github.com/NVIDIA/skills/archive/refs/heads/main.zip";

const CLONE_TABS: { id: CloneTab; label: string }[] = [
  { id: "https", label: "HTTPS" },
  { id: "ssh", label: "SSH" },
  { id: "cli", label: "GitHub CLI" },
];

export function AgentSkillsContent({
  skillFolderUrl,
}: {
  skillFolderUrl?: string;
}) {
  const [cloneTab, setCloneTab] = useState<CloneTab>("https");
  const [copied, setCopied] = useState(false);
  const [showAdvancedSetting, setShowAdvancedSetting] = useState(false);

  const handleCopy = () => {
    void navigator.clipboard.writeText(CLONE_VALUES[cloneTab]);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="flex flex-col gap-6 p-5">
      {/* Step 1 */}
      <div className="flex gap-4">
        <div className="flex-shrink-0 h-7 w-7 rounded-full bg-zinc-800 border border-zinc-700 flex items-center justify-center text-xs font-bold text-zinc-300">
          1
        </div>
        <div className="flex flex-col gap-2 flex-1 min-w-0">
          <p className="text-sm font-semibold text-white">
            {skillFolderUrl
              ? "Open this launchable's skill folder"
              : "Clone NVIDIA Physical AI Skills repository"}
          </p>
          {skillFolderUrl ? (
            <>
              <div className="rounded-md border border-zinc-700 px-3 py-2 bg-zinc-900">
                <code className="block text-xs text-white/75 font-mono overflow-x-auto whitespace-nowrap [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                  {skillFolderUrl}
                </code>
              </div>
              <a
                href={skillFolderUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex w-fit items-center gap-1.5 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-white/75 text-xs font-medium px-3 py-1.5 rounded-md transition-colors"
              >
                View skill folder
              </a>
            </>
          ) : (
            <>
              {/* GitHub-style clone box */}
              <div className="rounded-md border border-zinc-700 overflow-hidden">
                {/* Tab row */}
                <div className="flex border-b border-zinc-700 bg-zinc-800/60">
                  {CLONE_TABS.map(({ id, label }) => (
                    <button
                      key={id}
                      onClick={() => setCloneTab(id)}
                      className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                        cloneTab === id
                          ? "text-white border-b-2 border-[#76b900] -mb-px"
                          : "text-zinc-400 hover:text-zinc-200"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                {/* URL row */}
                <div className="flex items-center gap-2 px-3 py-2 bg-zinc-900">
                  <code className="flex-1 min-w-0 text-xs text-white/75 font-mono overflow-x-auto whitespace-nowrap [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                    {CLONE_VALUES[cloneTab]}
                  </code>
                  <button
                    onClick={handleCopy}
                    aria-label="Copy"
                    className="flex-shrink-0 text-zinc-400 hover:text-white transition-colors p-1 rounded"
                  >
                    {copied ? (
                      <Check className="h-4 w-4 text-[#76b900]" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>
              {/* Download alternative */}
              <div className="flex items-center gap-2">
                <span className="text-xs text-zinc-500">
                  Or download the skills directly
                </span>
                <a
                  href={DOWNLOAD_SKILLS_URL}
                  className="inline-flex items-center gap-1.5 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-white/75 text-xs font-medium px-3 py-1.5 rounded-md transition-colors"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-3.5 w-3.5 flex-shrink-0"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                    />
                  </svg>
                  Download skills
                </a>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Step 2 */}
      <div className="flex gap-4">
        <div className="flex-shrink-0 h-7 w-7 rounded-full bg-zinc-800 border border-zinc-700 flex items-center justify-center text-xs font-bold text-zinc-300">
          2
        </div>
        <div className="flex flex-col gap-2">
          <p className="text-sm font-semibold text-white">Use skills with</p>
          <div className="flex flex-wrap gap-2">
            {AGENT_TOOLS.map(({ name, logo }) => (
              <div
                key={name + logo}
                title={name}
                className="w-10 h-10 rounded-lg overflow-hidden border border-zinc-700 bg-zinc-800 flex items-center justify-center"
              >
                <img
                  src={logo}
                  alt={name}
                  className="w-full h-full object-cover"
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      {skillFolderUrl && (
        <div className="mt-1 border-t border-zinc-800 pt-4">
          <div
            className="flex flex-row cursor-pointer text-secondary hover:text-highlight items-center transition-colors"
            onClick={() => setShowAdvancedSetting(!showAdvancedSetting)}
          >
            <span className="text-sm font-semibold mr-1">Advanced</span>
            {showAdvancedSetting ? (
              <ChevronDownIcon className="w-4 h-4" />
            ) : (
              <ChevronRightIcon className="w-4 h-4" />
            )}
          </div>

          {showAdvancedSetting && (
            <div className="mt-3 pl-4 border-l-2 border-zinc-700">
              <p className="text-sm font-semibold text-white mb-2">
                Clone full repository
              </p>
              <div className="rounded-md border border-zinc-700 overflow-hidden">
                <div className="flex border-b border-zinc-700 bg-zinc-800/60">
                  {CLONE_TABS.map(({ id, label }) => (
                    <button
                      key={id}
                      onClick={() => setCloneTab(id)}
                      className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                        cloneTab === id
                          ? "text-white border-b-2 border-[#76b900] -mb-px"
                          : "text-zinc-400 hover:text-zinc-200"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-2 px-3 py-2 bg-zinc-900">
                  <code className="flex-1 min-w-0 text-xs text-white/75 font-mono overflow-x-auto whitespace-nowrap [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                    {CLONE_VALUES[cloneTab]}
                  </code>
                  <button
                    onClick={handleCopy}
                    aria-label="Copy"
                    className="flex-shrink-0 text-zinc-400 hover:text-white transition-colors p-1 rounded"
                  >
                    {copied ? (
                      <Check className="h-4 w-4 text-[#76b900]" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function PhysicalAIPipelineCard({ pipeline }: { pipeline: Pipeline }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const deployUrl = `${window.location.origin}/launchable/deploy?launchableID=${encodeURIComponent(pipeline.launchableId)}`;

  return (
    <>
      <GridBackground disableScale disableCursor className="h-full">
        <div className="border border-zinc-800 bg-primaryBg p-5 h-full flex flex-col rounded-md">
          <div className="flex flex-col gap-3 flex-grow">
            {/* Category tags */}
            <div className="flex flex-wrap gap-1.5">
              {pipeline.categories.map((cat) => (
                <Badge key={cat} variant="secondary" className="text-xs">
                  {cat}
                </Badge>
              ))}
            </div>

            {/* Title */}
            <h3 className="text-lg font-bold text-primary leading-tight">
              {pipeline.title}
            </h3>

            {/* Description */}
            <p className="text-sm text-secondary">{pipeline.description}</p>

            {/* Metadata */}
            <div className="flex items-center gap-4 text-xs text-secondary">
              <div className="flex items-center gap-1.5">
                <Cpu className="h-3.5 w-3.5 text-highlight" />
                <span>{pipeline.gpuRequirement}</span>
              </div>
            </div>

            {/* Powered by */}
            <p className="text-xs text-secondary">
              Powered by{" "}
              <span className="text-highlight font-medium">
                {pipeline.poweredBy}
              </span>
            </p>
          </div>

          {/* CTAs */}
          <div className="flex items-center gap-2 pt-5 mt-auto">
            <Button
              variant="secondary"
              className="text-sm flex-1"
              onClick={() => setDialogOpen(true)}
            >
              Deploy with your agent
            </Button>
            <Button
              variant="primary"
              className="text-sm flex-1"
              onClick={() =>
                window.open(deployUrl, "_blank", "noopener,noreferrer")
              }
            >
              Create agent
            </Button>
          </div>
        </div>
      </GridBackground>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg flex flex-col max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Launch on your cloud</DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto">
            <AgentSkillsContent skillFolderUrl={pipeline.skillFolderUrl} />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function AgentDeployModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg flex flex-col max-h-[80vh]">
        <DialogHeader>
          <DialogTitle>Launch on your cloud</DialogTitle>
        </DialogHeader>
        <div className="overflow-y-auto">
          <AgentSkillsContent />
        </div>
      </DialogContent>
    </Dialog>
  );
}

function YourCloudSection({ onCloudClick }: { onCloudClick: () => void }) {
  return (
    <section>
      <div
        className="relative rounded-xl border border-zinc-800 px-8 py-10 overflow-hidden"
        style={{ backgroundColor: "#0c0c0c" }}
      >
        {/* Honeycomb glow on the right to halo the cloud buttons */}
        <HoneycombBackground
          className="!opacity-80"
          svgTransform="translate(-300px, -125px)"
          flipX
        />
        {/* Left-fade so text sits on a clean dark background */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#0c0c0c] from-[40%] to-transparent pointer-events-none z-10" />

        {/* Content: text left, cloud tiles right */}
        <div className="relative z-20 flex items-center justify-between gap-12">
          {/* Left: heading + description + agent chips */}
          <div className="space-y-4">
            <div className="space-y-2">
              <h2 className="text-xl font-bold text-primary">
                Deploy to your cloud with your agent
              </h2>
              <p className="text-sm text-secondary leading-relaxed whitespace-nowrap">
                Every pipeline runs on your contracted GPU cloud using coding
                agents.
              </p>
            </div>

            {/* Agent logo chips */}
            <div className="flex items-center gap-2 flex-wrap">
              {AGENT_TOOLS.map(({ name, logo }) => (
                <div
                  key={name + logo}
                  title={name}
                  className="w-7 h-7 rounded-md overflow-hidden border border-zinc-700 bg-zinc-800 flex items-center justify-center"
                >
                  <img
                    src={logo}
                    alt={name}
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
            </div>

            {/* CTA hint */}
            <button
              onClick={onCloudClick}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-[#76b900] hover:text-[#8fd400] transition-colors"
            >
              Get started
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </div>

          {/* Right: cloud tiles */}
          <div className="flex gap-3 shrink-0">
            <button
              onClick={onCloudClick}
              aria-label="Deploy on Azure with your agent"
              className="group flex flex-col items-center justify-center gap-3 w-40 py-5 p-4 rounded-xl border border-zinc-700 bg-zinc-950 hover:border-[#76b900]/60 hover:bg-[#76b900]/5 hover:[background-image:radial-gradient(ellipse_at_center,rgb(0_0_0)_0%,rgba(0,0,0,0.2)_100%)] hover:shadow-[0_0_24px_rgba(118,185,0,0.2)] transition-all duration-200"
            >
              <Azure className="h-10 w-auto" />
              <span className="text-sm text-white transition-colors">
                Microsoft Azure
              </span>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

const PhysicalAIPage: React.FC = () => {
  const [agentModalOpen, setAgentModalOpen] = useState(false);

  return (
    <div className="relative w-full overflow-hidden">
      <HoneycombBackground />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 space-y-16">
        {/* Hero */}
        {/* Hero + Pipeline cards */}
        <section className="space-y-6">
          <div className="space-y-4">
            <p className="text-xs font-semibold uppercase tracking-widest text-highlight">
              NVIDIA Physical AI
            </p>
            <h1 className="text-4xl font-bold text-primary leading-tight max-w-2xl">
              Generate Synthetic Data
            </h1>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {PIPELINES.map((pipeline) => (
              <PhysicalAIPipelineCard
                key={pipeline.launchableId}
                pipeline={pipeline}
              />
            ))}
          </div>
        </section>

        {/* Deploy to your cloud with your agent */}
        <YourCloudSection onCloudClick={() => setAgentModalOpen(true)} />
      </div>

      <AgentDeployModal
        open={agentModalOpen}
        onClose={() => setAgentModalOpen(false)}
      />
    </div>
  );
};

export default PhysicalAIPage;
